import {
  Button,
  Input,
  Form,
  Col,
  Row,
  Icon,
  Steps,
  notification,
  Select,
  Switch,
} from "antd";
import withPermission from "../../../contexts/PermissionContext/withPermission";
import { useFreire } from "../../../utils/freireContext";
import * as texts from "../locales";
import SelectCountry from "../../../components/SelectCountry";
import SelectLocation from "../../../components/SelectLocation";
import { useCallback, useEffect, useState } from "react";
import { ICompany } from "../../../interfaces/company";
import { sorter } from "../../../utils/sorter";
import api from "../../../services/api";
import { COUNTRY_BR } from "../../../constants/countries";
import {
  getCountryCode,
  getStateAndCity,
} from "../../../utils/getLocationInfos";
import TextToolTip from "../../../components/TextToolTip";
import { getCompanyCurrentUser } from "../../../services/auth";
import { BRAND } from "../../../config";
import { DOCUMENT_TYPES } from "../../../constants/document-types";
import {
  formatDocumentByType,
  validateDocumentByType,
} from "../../../utils/document-masks";

const { Option } = Select;

const NO_ID = "noId";

function NewLocation(props: any) {
  const { freire } = useFreire();
  const { getFieldDecorator } = props.form;

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<ICompany[]>();
  const [tupiUser, setTupiUser] = useState<boolean>();
  const [useCompanyDocument, setUseCompanyDocument] = useState(true);

  const requiredFieldRule = {
    required: true,
    message: freire(texts.REQUIRED_FIELD),
  };

  const userHasOnlyOneCompany = companies?.length === 1;

  const initialCountry = userHasOnlyOneCompany
    ? companies[0]?.country
    : COUNTRY_BR;

  const selectedCountry = props.form.getFieldValue("country");
  const selectedCompanyId = props.form.getFieldValue("company_id");
  const hasCompanySelected = Boolean(
    selectedCompanyId && selectedCompanyId !== NO_ID
  );
  const selectedDocumentType = props.form.getFieldValue("document_type");
  const isDocumentOptional = selectedDocumentType === DOCUMENT_TYPES.CUIT;

  const getCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data: companies } = await api.get("company");

      const companiesNotHolding = sorter(
        companies.filter((company: ICompany) => !company.holding),
        "companyName"
      );
      setCompanies(companiesNotHolding);
    } catch (error) {
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.ERROR_LIST_COMPANIES),
      });
    } finally {
      setLoading(false);
    }
  }, [freire]);

  useEffect(() => {
    getCompanies();
    getUserCompanyID();
  }, [getCompanies]);

  const getUserCompanyID = async () => {
    const result = await getCompanyCurrentUser();
    if (result) {
      setTupiUser(result.id === BRAND && result.parentCompanyID === BRAND);
    }
  };

  const onSelectCountry = () => {
    props.form.resetFields(["address2.city", "companyID"]);
  };

  const handleLatLong = (value: any) => {
    const hasLatLong = value.includes(",");

    if (hasLatLong) {
      const [lat, lng] = value.split(", ");

      props.form.setFieldsValue({
        "coordinates.latitude": lat,
        "coordinates.longitude": lng,
      });
    }
  };

  const clearDocumentFields = useCallback(() => {
    props.form.setFieldsValue({
      document: "",
    });

    props.form.validateFields(["document"], { force: true }, () => undefined);
  }, [props.form]);

  const applyCompanyDocument = useCallback(
    (company: any) => {
      if (!company) {
        return;
      }

      const companyDocumentType = company?.document_type ?? DOCUMENT_TYPES.CNPJ;
      const formattedDocument = formatDocumentByType(
        company?.document ?? "",
        companyDocumentType
      );

      props.form.setFieldsValue({
        document_type: companyDocumentType,
        document: formattedDocument,
      });

      props.form.validateFields(["document"], { force: true }, () => undefined);
    },
    [props.form]
  );

  const getCompanyById = useCallback(
    (companyId?: string | null) => {
      if (!companyId || !companies) {
        return undefined;
      }

      return (companies as any[]).find((company) => company._id === companyId);
    },
    [companies]
  );

  const handleUseCompanyDocumentToggle = useCallback(
    (checked: boolean) => {
      setUseCompanyDocument(checked);

      if (!checked) {
        props.form.validateFields(
          ["document"],
          { force: true },
          () => undefined
        );
        return;
      }

      const companyId = props.form.getFieldValue("company_id");
      const effectiveCompanyId =
        companyId && companyId !== NO_ID ? companyId : undefined;

      if (!effectiveCompanyId) {
        return;
      }

      const company = getCompanyById(effectiveCompanyId);

      if (company) {
        applyCompanyDocument(company);
      }
    },
    [applyCompanyDocument, getCompanyById, props.form]
  );

  const handleCompanyChange = useCallback(
    (value?: string) => {
      props.form.setFieldsValue({ company_id: value });

      const isCompanySelected = value && value !== NO_ID;

      if (!isCompanySelected) {
        if (useCompanyDocument) {
          setUseCompanyDocument(false);
          clearDocumentFields();
        }
        return;
      }

      const company = getCompanyById(value);

      if (useCompanyDocument && company) {
        applyCompanyDocument(company);
      }
    },
    [
      applyCompanyDocument,
      clearDocumentFields,
      getCompanyById,
      props.form,
      useCompanyDocument,
    ]
  );

  const validateLocationDocument = useCallback(
    (_rule: any, value: string, callback: any) => {
      const documentType = props.form.getFieldValue("document_type");
      const trimmedValue = value?.trim();

      if (documentType === DOCUMENT_TYPES.CUIT) {
        if (!trimmedValue) {
          callback();
          return;
        }
      } else if (!trimmedValue) {
        callback(freire(texts.REQUIRED_FIELD));
        return;
      }

      if (trimmedValue && !validateDocumentByType(trimmedValue, documentType)) {
        callback(freire(texts.INVALID_DOCUMENT_FORMAT));
        return;
      }

      callback();
    },
    [props.form, freire]
  );

  const handleDocumentTypeChange = useCallback(
    (value: string) => {
      const currentDocument = props.form.getFieldValue("document") ?? "";

      props.form.setFieldsValue({
        document_type: value,
        document: formatDocumentByType(currentDocument, value),
      });

      props.form.validateFields(["document"], { force: true }, () => undefined);
    },
    [props.form]
  );

  const onSave = async () => {
    setLoading(true);
    props.form.validateFields(async (err: any, location: any) => {
      if (err) {
        setLoading(false);
        return;
      }

      if (location.address2) {
        const { state, city } = getStateAndCity(
          location.country,
          location.address2
        );

        location.state = state;
        location.city = city;

        delete location.address2;
      }

      if (location.company_id === NO_ID) {
        location.company_id = null;
      }

      if (location.company_id) {
        const selectedCompany = companies?.find(
          (company) => company._id === location.company_id
        );

        location.parent_company_id = selectedCompany?.parentCompanyID;
        location.currency = selectedCompany?.currency;
        location.language = selectedCompany?.language;
      }

      if (useCompanyDocument) {
        const company = getCompanyById(location.company_id);
        if (company) {
          const companyDocumentType =
            company?.document_type ?? DOCUMENT_TYPES.CNPJ;
          location.document_type = companyDocumentType;
          location.document = formatDocumentByType(
            company?.document ?? "",
            companyDocumentType
          );
        }
      } else if (!location.document?.trim()) {
        location.document = null;
      }

      if (location.country) {
        location.country_code = getCountryCode(location.country);
      }

      try {
        await api.post("locations", location);
        notification.success({
          message: freire(texts.ALL_RIGHT),
          description: freire(texts.LOCATION_ADD_SUCCESS),
        });
        props.history.push("/dashboard/list_locations");
      } catch (error: any) {
        const message = error?.response?.data?.message;

        notification.error({
          message: freire(texts.WENT_WRONG),
          description: message || error.message,
        });
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <div className="container">
      <div className="contentFull">
        <Row>
          <Col span={4}>
            <Steps
              current={1}
              size="small"
              onChange={() => {}}
              direction="vertical"
            >
              <Steps.Step
                icon={<Icon type="profile" />}
                title={freire(texts.LOCATIONS)}
                description={freire(texts.ADD_LOCATION)}
              />
            </Steps>
          </Col>

          <Col span={20}>
            <Form wrapperCol={{ span: 24 }}>
              <Row gutter={[16, 0]}>
                <Col span={24}>
                  <Form.Item label={freire(texts.NAME)}>
                    {getFieldDecorator("name", {
                      rules: [requiredFieldRule],
                    })(<Input placeholder={freire(texts.NAME)} />)}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <SelectCountry
                    form={props.form}
                    label={texts.COUNTRY}
                    disabled={userHasOnlyOneCompany}
                    initialValue={initialCountry}
                    showWarn={false}
                    onSelect={onSelectCountry}
                  />
                </Col>
                <Col span={6}>
                  <Form.Item label={freire(texts.LATITUDE)}>
                    {getFieldDecorator("coordinates.latitude", {
                      rules: [requiredFieldRule],
                    })(
                      <Input
                        onBlur={(e) => handleLatLong(e.target.value)}
                        style={{ width: "100%" }}
                        placeholder={freire(texts.LATITUDE_EX)}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={freire(texts.LONGITUDE)}>
                    {getFieldDecorator("coordinates.longitude", {
                      rules: [requiredFieldRule],
                    })(
                      <Input
                        style={{ width: "100%" }}
                        placeholder={freire(texts.LATITUDE_EX)}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span>
                        {freire(texts.ADDRESS)}
                        <TextToolTip
                          title={freire(texts.ADDRESS)}
                          icon="question-circle-o"
                          info={[{ info: freire(texts.ADDRESS_TOOLTIP_TEXT) }]}
                        />
                      </span>
                    }
                  >
                    {getFieldDecorator("address", {
                      rules: [requiredFieldRule],
                    })(<Input placeholder={freire(texts.ADDRESS)} />)}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <SelectLocation
                  required={true}
                  form={props.form}
                  countryCode={selectedCountry}
                />
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={freire(texts.COMPANY)}
                    extra={freire(texts.SELECT_COMPANY_WARN)}
                  >
                    {getFieldDecorator("company_id", {
                      rules: !tupiUser && [requiredFieldRule],
                    })(
                      <Select
                        placeholder={freire(texts.SELECT_COMPANY)}
                        showSearch
                        optionFilterProp="children"
                        onChange={handleCompanyChange}
                      >
                        {tupiUser && (
                          <Select.Option
                            label={freire(texts.WITHOUT_COMPANY)}
                            value={NO_ID}
                            key={NO_ID}
                            style={{ borderBottom: "1px solid #D4D4D3" }}
                          >
                            {freire(texts.WITHOUT_COMPANY)}
                          </Select.Option>
                        )}
                        {companies &&
                          companies.map((company) => (
                            <Select.Option
                              disabled={company.country !== selectedCountry}
                              key={company._id}
                              value={company._id}
                            >
                              {company.companyName}
                            </Select.Option>
                          ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col span={24}>
                  <Form.Item colon={false} label={null}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Switch
                        checked={useCompanyDocument}
                        onChange={handleUseCompanyDocumentToggle}
                        disabled={!hasCompanySelected}
                        checkedChildren={freire(texts.YES)}
                        unCheckedChildren={freire(texts.NO)}
                      />
                      <span>{freire(texts.USE_COMPANY_DOCUMENT)}</span>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item label={freire(texts.DOCUMENT_TYPE)} required>
                    {getFieldDecorator("document_type", {
                      initialValue: DOCUMENT_TYPES.CNPJ,
                      rules: [requiredFieldRule],
                    })(
                      <Select
                        placeholder={freire(texts.DOCUMENT_TYPE)}
                        onChange={handleDocumentTypeChange}
                        disabled={useCompanyDocument}
                      >
                        {Object.values(DOCUMENT_TYPES).map((value) => (
                          <Option key={value} value={value}>
                            {value}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={freire(texts.NUMBER_OF_DOCUMENT)}
                    required={!isDocumentOptional}
                  >
                    {getFieldDecorator("document", {
                      getValueFromEvent: (event: any) =>
                        formatDocumentByType(
                          event.target.value,
                          props.form.getFieldValue("document_type")
                        ),
                      rules: [
                        {
                          validator: validateLocationDocument,
                        },
                      ],
                    })(
                      <Input
                        placeholder={"Ex.: 12.345.678/0009-10"}
                        disabled={useCompanyDocument}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item label={freire(texts.PHONE)}>
                    {getFieldDecorator("phone")(
                      <Input placeholder={freire(texts.PHONE)} />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={freire(texts.PRIVATE_LOCATION_QUESTION)}>
                    {getFieldDecorator("private", {
                      valuePropName: "checked",
                    })(
                      <Switch
                        checkedChildren={freire(texts.YES)}
                        unCheckedChildren={freire(texts.NO)}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label={freire(texts.FREE_PARKING_QUESTION)}>
                    {getFieldDecorator("freeParking", {
                      valuePropName: "checked",
                    })(
                      <Switch
                        checkedChildren={freire(texts.YES)}
                        unCheckedChildren={freire(texts.NO)}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
          <div className="footer">
            <Button loading={loading} onClick={onSave} type="primary">
              {freire(texts.SAVE)}
            </Button>
          </div>
        </Row>
      </div>
    </div>
  );
}

const PageNewLocation = Form.create({ name: "form" })(NewLocation);
export default withPermission(PageNewLocation);
